import React, { useState, useRef, useEffect } from 'react'
import tw, { styled } from 'twin.macro'
import PropTypes from 'prop-types'
import { graphql, useStaticQuery, Link } from 'gatsby'
import Img from 'gatsby-image'

const DEFAULT_VISIBLE = 3

const Section = styled.section`
	background-color: #d6ecf7;
	padding: 3rem 2rem;
	text-align: center;
	/* Full-width breakout from parent container */
	width: 100vw;
	position: relative;
	left: 50%;
	transform: translateX(-50%);
	@media (max-width: 640px) {
		padding: 2rem 1rem;
	}
`

const Heading = styled.h2`
	font-weight: 800;
	font-size: clamp(1.4rem, 3vw, 2.8rem);
	color: #000;
	margin-bottom: 2rem;
	font-family: Flexo, system-ui, sans-serif !important;
	
`

const CarouselWrapper = styled.div`
	${tw`flex items-center justify-center`}
	gap: 1rem;
	max-width: 1340px;
	margin: 0 auto;
	@media (max-width: 640px) {
		gap: 0.5rem;
	}
`

const CarouselTrack = styled.div`
	flex: 1;
	min-width: 0;
	max-width: 1100px;
	overflow: hidden;
`

const CardsInner = styled.div`
	display: flex;
	transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
`

const Card = styled.div`
	background: white;
	border-radius: 3rem;
	aspect-ratio: 1 / 1;
	box-sizing: border-box;
	flex-shrink: 0;
	position: relative;
	overflow: hidden;
	@media (max-width: 640px) {
		border-radius: 1.75rem;
	}
`

const CardImageWrapper = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 3.5rem;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 1rem;
`


const CardLabelWrapper = styled.div`
	position: absolute;
	bottom: 1.75rem;
	right: 1.75rem;
	display: flex;
	justify-content: flex-end;
	@media (max-width: 640px) {
		bottom: 1rem;
		right: 1rem;
	}
`

const CardLabel = styled.span`
	border: 1.5px solid #aac7d8;
	border-radius: 999px;
	padding: 0.3rem 1rem;
	font-size: 0.85rem;
	font-weight: 600;
	color: #333;
	white-space: nowrap;
	@media (max-width: 640px) {
		font-size: 0.7rem;
		padding: 0.25rem 0.6rem;
	}
`

const NavButton = styled.button`
	background: white;
	border: none;
	border-radius: 50%;
	width: 60px;
	height: 60px;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
	flex-shrink: 0;
	transition: background 0.2s;
	color: #374151;
	&:hover {
		background: #f0f0f0;
	}
	&:disabled {
		opacity: 0.35;
		cursor: not-allowed;
	}
	@media (max-width: 640px) {
		width: 40px;
		height: 40px;
	}
`

const DotsRow = styled.div`
	display: flex;
	justify-content: center;
	gap: 0.5rem;
	margin-top: 1.5rem;
`

const AchievementsCarousel = ({ locale }) => {
	const { achievement1, achievement2, achievement3, achievement4, posts, posts2, posts3, posts4 } = useStaticQuery(achievementsCarouselQuery)

	const achievements = [
		{ id: '1', label: '40+ Years of Innovation', image: achievement1?.nodes?.[0], slug: posts?.nodes?.[0]?.slug },
		{ id: '2', label: 'SPASA Awards', image: achievement2?.nodes?.[0], slug: posts2?.nodes?.[0]?.slug },
		{ id: '3', label: '2025 Best Innovations by TIME', image: achievement3?.nodes?.[0], slug: posts3?.nodes?.[0]?.slug },
		{ id: '4', label: '40+ Years of Innovation', image: achievement4?.nodes?.[0], slug: posts4?.nodes?.[0]?.slug },
	]
	const [offset, setOffset] = useState(0)
	const trackRef = useRef(null)
	const [trackWidth, setTrackWidth] = useState(0)

	useEffect(() => {
		const update = () => {
			if (trackRef.current) setTrackWidth(trackRef.current.offsetWidth)
		}
		update()
		window.addEventListener('resize', update)
		return () => window.removeEventListener('resize', update)
	}, [])

	const visibleCount = trackWidth < 480 ? 1 : DEFAULT_VISIBLE
	const gapPx = trackWidth < 480 ? 12 : 24

	const maxOffset = Math.max(0, achievements.length - visibleCount)
	const totalDots = maxOffset + 1

	useEffect(() => {
		setOffset(o => Math.min(o, maxOffset))
	}, [maxOffset])

	const cardWidth = trackWidth > 0
		? Math.floor((trackWidth - (visibleCount - 1) * gapPx) / visibleCount) - 1
		: 0
	const translateX = -offset * (cardWidth + gapPx)

	return (
		<Section>
			<Heading>Our Achievements</Heading>

			<CarouselWrapper>
				<NavButton
					onClick={() => setOffset(o => o - 1)}
					disabled={offset === 0}
					aria-label="Previous"
				>
					<svg width="9" height="15" viewBox="0 0 9 15" fill="none">
						<path d="M7.5 1L1 7.5L7.5 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
					</svg>
				</NavButton>

				<CarouselTrack ref={trackRef}>
					<CardsInner style={{ transform: `translateX(${translateX}px)`, gap: `${gapPx}px` }}>
						{achievements.map(item => (
							<Card
								key={item.id}
								style={{ width: cardWidth > 0 ? `${cardWidth}px` : undefined }}
							>
								<CardImageWrapper>
									{item.image?.fluid && (
										<Img
											fluid={item.image.fluid}
											alt={item.image.alt || item.image.title || item.label}
											style={{ width: '100%' }}
											imgStyle={{ objectFit: 'contain', objectPosition: 'center' }}
										/>
									)}
								</CardImageWrapper>
								<CardLabelWrapper>
									{item.slug ? (
										<Link
											to={`/${item.slug}/`}
											aria-label={item.label}
											style={{ textDecoration: 'none' }}
										>
											<CardLabel style={{ cursor: 'pointer', transition: 'all 0.2s', display: 'block' }}>
												{item.label}
											</CardLabel>
										</Link>
									) : (
										<CardLabel>{item.label}</CardLabel>
									)}
								</CardLabelWrapper>
							</Card>
						))}
					</CardsInner>
				</CarouselTrack>

				<NavButton
					onClick={() => setOffset(o => o + 1)}
					disabled={offset >= maxOffset}
					aria-label="Next"
				>
					<svg width="9" height="15" viewBox="0 0 9 15" fill="none">
						<path d="M1.5 1L8 7.5L1.5 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
					</svg>
				</NavButton>
			</CarouselWrapper>

			<DotsRow>
				{Array.from({ length: totalDots }).map((_, i) => (
					<button
						key={i}
						onClick={() => setOffset(i)}
						aria-label={`Go to item ${i + 1}`}
						style={{
							width: '24px',
							height: '24px',
							borderRadius: '50%',
							border: '3px solid white',
							padding: 0,
							cursor: 'pointer',
							transition: 'all 0.2s',
							backgroundColor: i === offset ? '#2d2d2d' : 'transparent',
						}}
					/>
				))}
			</DotsRow>
		</Section>
	)
}

AchievementsCarousel.propTypes = {
	locale: PropTypes.string.isRequired,
}

export const achievementsCarouselQuery = graphql`
	query achievementsCarouselQuery {
		achievement1: allContentfulAsset(
			filter: {
				contentful_id: { eq: "2CT1HBxtxlvpOWigx5zCxR" }
				node_locale: { eq: "en-AU" }
			}
		) {
			nodes {
				title
				alt: description
				fluid(maxWidth: 300, maxHeight: 300, quality: 80) {
					...GatsbyContentfulFluid_withWebp
				}
			}
		}
		posts: allContentfulPost(
			filter: {
				node_locale: { eq: "en-AU" }
				contentful_id: {
					in: [
						"ztpfh1AHM2YNSQycrWbTM"
					]
				}
			}
		) {
			nodes {
				contentful_id
				slug
			}
		}
		achievement2: allContentfulAsset(
			filter: {
				contentful_id: { eq: "3I4J2QKO8gLJsA8zL9FJQr" }
				node_locale: { eq: "en-AU" }
			}
		) {
			nodes {
				title
				alt: description
				fluid(maxWidth: 300, maxHeight: 300, quality: 80) {
					...GatsbyContentfulFluid_withWebp
				}
			}
		}
		posts2: allContentfulPost(
			filter: {
				node_locale: { eq: "en-AU" }
				contentful_id: {
					in: [
						"6v3JOic1czWeYZZ6PYgixL"
					]
				}
			}
		) {
			nodes {
				contentful_id
				slug
			}
		}
		achievement3: allContentfulAsset(
			filter: {
				contentful_id: { eq: "2tE0UyzrAjdZ2HBtSD0VRB" }
				node_locale: { eq: "en-AU" }
			}
		) {
			nodes {
				title
				alt: description
				fluid(maxWidth: 300, maxHeight: 300, quality: 80) {
					...GatsbyContentfulFluid_withWebp
				}
			}
		}
		posts3: allContentfulPost(
			filter: {
				node_locale: { eq: "en-AU" }
				contentful_id: {
					in: [
						"642l5xsij85e8aaQU4YnNO"
					]
				}
			}
		) {
			nodes {
				contentful_id
				slug
			}
		}
		achievement4: allContentfulAsset(
			filter: {
				contentful_id: { eq: "2CT1HBxtxlvpOWigx5zCxR" }
				node_locale: { eq: "en-AU" }
			}
		) {
			nodes {
				title
				alt: description
				fluid(maxWidth: 300, maxHeight: 300, quality: 80) {
					...GatsbyContentfulFluid_withWebp
				}
			}
		}
		posts4: allContentfulPost(
			filter: {
				node_locale: { eq: "en-AU" }
				contentful_id: {
					in: [
						"ztpfh1AHM2YNSQycrWbTM"
					]
				}
			}
		) {
			nodes {
				contentful_id
				slug
			}
		}
	}
`

export default AchievementsCarousel
